import { FC, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminCarsApi } from "../../http/cars/api";
import { CarNoCarDepency } from "../../http/cars/types";
import { useLeasingCompaniesApi } from "../../http/leasingCompanies/api";
import { LeasingCompany } from "../../http/leasingCompanies/types";
import { useManufacturersApi } from "../../http/manufacturers/api";
import { Manufacturer } from "../../http/manufacturers/types";
import { RentalCar } from "../../http/rentalCars/types";
import { useUsersApi } from "../../http/users/api";
import { User } from "../../http/users/types";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import reactSelectStyles from "../ui/ReactSelectUtils";

const CAR_FIELDS = ["id", "vehicleIdentificationNumber", "currentPlateNumber", "lastPlateNumber"] as const;
type CarField = (typeof CAR_FIELDS)[number];
type CarSearch = Pick<CarNoCarDepency, CarField>;

const USER_FIELDS = ["id", "fullName"] as const;
type UserField = (typeof USER_FIELDS)[number];
type UserSearch = Pick<User, UserField>;

const LEASING_COMPANY_FIELDS = ["id", "name"] as const;
type LeasingCompanyFiels = (typeof LEASING_COMPANY_FIELDS)[number];
type LeasingCompanySearch = Pick<LeasingCompany, LeasingCompanyFiels>;

const MANUFACTURER_FIELDS = ["id", "name"] as const;
type ManufacturerField = (typeof MANUFACTURER_FIELDS)[number];
type ManufacturerSearch = Pick<Manufacturer, ManufacturerField>;

export interface RentalCarFormBody {
  id: string;
  car: {
    id: string;
    vehicleIdentificationNumber: string;
    currentPlateNumber?: string;
    lastPlateNumber?: string;
  };
  driver: UserSearch;
  startDate: string;
  endDate?: string;
  plateNumber: string;
  leasingCompany: LeasingCompanySearch;
  manufacturer?: ManufacturerSearch;
  model?: string;
}

type RentalCarFormProps = {
  rentalCar?: RentalCar | null;
  onSubmit: (form: RentalCarFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  carId?: string;
  driver?: User;
};

const RentalCarForm: FC<RentalCarFormProps> = ({
  rentalCar,
  onSubmit,
  isDisabled,
  isLoading,
  carId,
  driver,
}: RentalCarFormProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const carsApi = useAdminCarsApi();
  const usersApi = useUsersApi();
  const leasingCompaniesApi = useLeasingCompaniesApi();
  const manufacturerApi = useManufacturersApi();

  const [defaultUserOptions, setDefaultUserOptions] = useState<UserSearch[]>([]);
  const [defaultCarOptions, setDefaultCarOptions] = useState<CarSearch[]>([]);

  const leasingCompanies = leasingCompaniesApi.useAdminLeasingCompanies(
    { limit: 100, fields: LEASING_COMPANY_FIELDS },
    aborter.current.signal,
  );

  const manufacturers = manufacturerApi.useAdminManufacturers(
    { limit: 100, fields: MANUFACTURER_FIELDS },
    aborter.current.signal,
  );

  const defaultValues = {
    car: rentalCar?.car,
    startDate: rentalCar?.startDate,
    endDate: rentalCar?.endDate,
    driver: rentalCar?.driver || driver,
    plateNumber: rentalCar?.plateNumber,
    manufacturer: rentalCar?.manufacturer,
    model: rentalCar?.model,
    leasingCompany: rentalCar?.leasingCompany,
  };

  return (
    <Form<RentalCarFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => (formData) => onSubmit(formData)}
    >
      {({ control, register, formState: { errors }, reset, watch, setValue }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [rentalCar || driver]);

        return (
          <FormFieldsContainer>
            {!carId && (
              <>
                <Controller
                  name="car"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.t.cars.singular} error={fieldState.error}>
                      {({ labelId, isInvalid }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions<CarSearch, CarField>(
                          aborter,
                          searchTerm,
                          carsApi.getCars,
                          { fields: CAR_FIELDS },
                        );

                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultCarOptions);
                        }, []);

                        return (
                          <AsyncSelect
                            menuPosition="fixed"
                            {...field}
                            inputId={labelId}
                            isDisabled={isDisabled || isLoading}
                            isLoading={isLoading}
                            defaultOptions={defaultCarOptions}
                            loadOptions={loadOptions}
                            placeholder={`${i18n.t.common.select}...`}
                            getOptionLabel={(x) =>
                              x.currentPlateNumber || x.lastPlateNumber || x.vehicleIdentificationNumber
                            }
                            getOptionValue={(x) => x.id}
                            styles={reactSelectStyles<{
                              id: string;
                              vehicleIdentificationNumber: string;
                              currentPlateNumber?: string;
                              lastPlateNumber?: string;
                            }>(isInvalid)}
                          />
                        );
                      }}
                    </FormField>
                  )}
                />
              </>
            )}
            <FormField label={i18n.t.plateNumbers.singular} error={errors.plateNumber}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("plateNumber", {
                    minLength: 1,
                    required: true,
                  })}
                />
              )}
            </FormField>
            <Controller
              name="leasingCompany"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.leasingCompanies.singular} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Select
                      menuPosition="fixed"
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading}
                      isLoading={isLoading}
                      options={leasingCompanies.data?.items}
                      placeholder={`${i18n.t.common.select}...`}
                      getOptionLabel={(x) => x.name}
                      getOptionValue={(x) => x.id}
                      styles={reactSelectStyles<LeasingCompanySearch>(isInvalid)}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="manufacturer"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.manufacturers.singular} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Select
                      menuPosition="fixed"
                      {...field}
                      inputId={labelId}
                      options={manufacturers.data?.items}
                      isLoading={isLoading}
                      isDisabled={isDisabled || isLoading}
                      placeholder={`${i18n.t.common.select}...`}
                      getOptionLabel={(x) => x.name}
                      getOptionValue={(x) => x.id}
                      styles={reactSelectStyles<ManufacturerSearch>(isInvalid)}
                      isClearable
                    />
                  )}
                </FormField>
              )}
            />

            <FormField label={i18n.t.common.model} error={errors.model} isOptional>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("model", {
                    minLength: 1,
                  })}
                />
              )}
            </FormField>

            <Controller
              name="driver"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.drivers.singular} error={fieldState.error}>
                  {({ labelId, isInvalid }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());

                    const loadOptions = createLoadOptions<UserSearch, UserField>(
                      aborter,
                      searchTerm,
                      usersApi.getAdminUsers,
                      {
                        fields: USER_FIELDS,
                      },
                    );

                    // Fetch initial data on mount
                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultUserOptions);
                    }, []);

                    return (
                      <AsyncSelect
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        isDisabled={isDisabled || isLoading}
                        isLoading={isLoading}
                        defaultOptions={defaultUserOptions}
                        loadOptions={loadOptions}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.fullName}
                        getOptionValue={(x) => x.id}
                        styles={reactSelectStyles<UserSearch>(isInvalid)}
                      />
                    );
                  }}
                </FormField>
              )}
            />
            <Controller
              name="startDate"
              control={control}
              rules={{
                required: true,
              }}
              render={({ fieldState }) => (
                <FormField label={i18n.t.common.startDate} error={fieldState.error}>
                  {({ isInvalid }) => (
                    <DatePicker
                      onChange={(v) => setValue("startDate", v!)}
                      isInvalid={isInvalid}
                      value={watch("startDate")}
                      disabled={isDisabled || isLoading}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="endDate"
              control={control}
              render={({ fieldState }) => (
                <FormField label={i18n.t.common.endDate} error={fieldState.error} isOptional>
                  {({ isInvalid }) => (
                    <DatePicker
                      onChange={(v) => setValue("endDate", v!)}
                      isInvalid={isInvalid}
                      value={watch("endDate")}
                      disabled={isDisabled || isLoading}
                      clearable
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default RentalCarForm;
